import type React from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { type SubLink } from '@cx-web/domain-types';
import classnames from 'classnames';

import Icon from '../../../../primitives/icon/index';

import styles from './ToolsDropdown.module.css';

type ToolsDropdownProps = {
  iconSize?: number;
  isLoggedIn: boolean;
  items: SubLink[];
  legacyToggle?: boolean;
  logoutLabel: string;
  onLogout: () => void;
};

const isExternal = (link?: string) => !!link && link.startsWith('http');

const ToolsDropdown: React.FC<ToolsDropdownProps> = ({
  iconSize = 18,
  isLoggedIn,
  items,
  legacyToggle = false,
  logoutLabel,
  onLogout,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Unique per instance so aria-controls/id stays valid if more than one dropdown renders on a page.
  const panelId = useId();

  const handleLogout = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsOpen(false);
      onLogout();
    },
    [onLogout],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className={styles.container} data-open={isOpen} data-testid="tools-dropdown" ref={containerRef}>
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        aria-label="Tools"
        className={classnames(styles.toggle, legacyToggle && styles.toggleLegacy)}
        data-testid="tools-dropdown-trigger"
        onClick={() => setIsOpen(prev => !prev)}
        type="button"
      >
        <Icon height={iconSize} icon="HammerSpanner" width={iconSize} />
      </button>

      <div className={styles.panelWrapper}>
        <div className={styles.panel} id={panelId}>
          <ul className={styles.list}>
            {items.map((item, index) => (
              <li className={styles.item} key={item.link || item.name || index}>
                <a
                  className={styles.link}
                  href={item.link}
                  rel={isExternal(item.link) ? 'noopener noreferrer' : undefined}
                  target={isExternal(item.link) ? '_blank' : undefined}
                >
                  {item.name}
                </a>
              </li>
            ))}
            {isLoggedIn && (
              <li className={styles.item}>
                <button className={styles.logoutButton} onClick={handleLogout} type="button">
                  {logoutLabel}
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ToolsDropdown;
